"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, Phone, Mail, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
export default function Upload() {
  const shouldReduceMotion = useReducedMotion();
  const reviewHighlights = [{
    title: "Upload Recent Invoices",
    desc: "Attach your latest electricity or gas bill to extract exact MPRN/GPRN and tariff details."
  }, {
    title: "Full Tariff & Market Comparison",
    desc: "We analyze unit rates, standing charges, capacity charges, and current supplier discounts."
  }, {
    title: "Direct Specialist Review",
    desc: "An energy advisor will review your figures and contact you with clear recommendations."
  }];
  return <section id="upload-a-bill-request-a-call-back" data-nav="light" className="bg-background text-foreground py-12 md:py-16 border-t border-border">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-muted border border-border text-xs font-bold text-foreground uppercase tracking-wider mb-4">
            <span className="size-2 rounded-lg bg-primary" />
            Commercial Energy Review
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-4xl font-bold text-foreground tracking-tight leading-tight mb-4">
            Upload a Recent Bill and Request a Call Back
          </h2>

          {/* Decorative gradient rule */}
          <div className="w-24 h-1 mx-auto rounded-lg bg-linear-to-r from-primary/80 via-primary to-primary/60 mb-4" />

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Send us a recent electricity or gas bill so we can identify the meter, current tariff and relevant account information. We will contact you to discuss the options that may be available.
          </p>
        </div>

        {/* 2-Column Structured Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Direct Bigin Review Form Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7">
            <Card className="border border-border bg-card shadow-lg rounded-2xl overflow-hidden">
              <CardHeader className="border-b border-border/60 bg-muted/20 pb-5">
                <div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    Request an Electricity or Gas Review
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground mt-1">
                    Complete our secure online intake form to upload your bill and initiate your tariff assessment.
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="pt-6 space-y-6">
                <div className="space-y-4">
                  {reviewHighlights.map((item, index) => <div key={index} data-index={index} className="flex items-start gap-3.5 p-3.5 rounded-xl border border-border/70 bg-muted/30">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle2 className="size-4.5" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground">{item.title}</h4>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>)}
                </div>

                <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-3">
                  <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                    <FileText className="size-4 text-primary shrink-0" />
                    <span>What to have ready:</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    A copy or photo of a recent commercial electricity or gas bill (last 3 months) showing your MPRN/GPRN, current contract end date, and consumption profile.
                  </p>
                </div>

                <div className="space-y-3 pt-2">
                  <Button asChild size="lg" className="w-full h-12 text-base font-semibold shadow-md gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/FreeEnergyReviewPage" className="flex items-center justify-center gap-2">
                      <span>Open Electricity &amp; Gas Review Form</span>
                      <ArrowRight className="size-4 shrink-0" />
                    </Link>
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
                    <ShieldCheck className="size-3.5 text-primary shrink-0" />
                    <span>Secure encrypted submission powered by Bigin CRM</span>
                  </div>
                </div>

                <p className="text-[11px] text-center text-muted-foreground leading-relaxed border-t border-border/60 pt-4">
                  Your information will be used strictly to review your tariff enquiry and contact you. Where necessary and authorised, relevant data may be shared with energy suppliers or auditors in accordance with our Privacy Policy.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column: Advisory Information & Direct Contact */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 space-y-6">
            {/* Commercial Project Coordination Card */}
            <div className="p-6 rounded-2xl border border-border bg-card shadow-md relative overflow-hidden space-y-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                  <span className="size-1.5 rounded-lg bg-primary mr-1.5" />
                  Commercial Project Coordination
                </span>
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground mb-1.5">
                  End-to-End Energy Upgrades &amp; Advisory
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  CMI coordinates qualified technical experts and vetted contractors for commercial solar, HVAC, heat pumps, and lighting upgrades to optimize efficiency and reduce baseline consumption.
                </p>
              </div>
              <div className="p-3.5 rounded-xl bg-muted/40 border border-border/80">
                <p className="text-xs font-semibold text-foreground leading-relaxed">
                  There is no fee charged by CMI to the customer for our service.
                </p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  We work alongside your management team to evaluate opportunities, streamline supplier proposals, and ensure full transparency from initial assessment through to implementation.
                </p>
              </div>
            </div>

            {/* Direct Contact Card */}
            <div className="p-5 rounded-2xl border border-border bg-muted/40 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Prefer to speak directly?</p>
                <p className="text-sm font-bold text-foreground">Call our energy desk</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button asChild variant="outline" size="sm" className="bg-card text-foreground hover:text-foreground">
                  <a href="tel:040255333" className="flex items-center gap-1.5 text-xs font-semibold">
                    <Phone className="size-3.5 text-primary" />
                    0402 55333
                  </a>
                </Button>
                <Button asChild variant="outline" size="sm" className="bg-card text-foreground hover:text-foreground">
                  <Link to="/ContactPage" className="flex items-center gap-1.5 text-xs font-semibold">
                    <Mail className="size-3.5 text-primary" />
                    Email
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}